/**
 * Edit-specific styled primitives that extend the shared GKVNew token system.
 *
 * Only includes components unique to the edit flow (document links, stepper, notes).
 * Generic primitives (cards, tables, summary, headers) are imported from GKVNew/styles/primitives.
 */
import { styled } from "@mui/material/styles";
import { Box, TextField } from "@mui/material";
import { colors, fontSize, radius, spacing } from "../../GKVNew/styles/tokens";

// ═══════════════════════════════════════════════════════════════
// DOCUMENT LINK PRIMITIVES
// ═══════════════════════════════════════════════════════════════

/** Row of document links (PDF download buttons) */
export const DocumentLinksRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: spacing.md,
  padding: `${spacing.sm} 0`,
  flexWrap: "wrap",
});

/** Single document link chip */
export const DocumentLink = styled("a")({
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.xs,
  padding: `2px ${spacing.md}`,
  borderRadius: radius.sm,
  fontSize: fontSize.label,
  fontWeight: 500,
  color: colors.info,
  backgroundColor: "rgba(67, 97, 255, 0.08)",
  textDecoration: "none",
  transition: "background-color 0.15s ease",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "rgba(67, 97, 255, 0.16)",
  },
});

// ═══════════════════════════════════════════════════════════════
// NOTES PRIMITIVES
// ═══════════════════════════════════════════════════════════════

/** Compact notes text field */
export const NotesField = styled(TextField)({
  "& .MuiInputBase-root": {
    fontSize: fontSize.data,
    padding: spacing.sm,
  },
  "& .MuiInputBase-input": {
    padding: `${spacing.sm} ${spacing.md}`,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: colors.borderLight,
    borderRadius: radius.sm,
  },
  "& .MuiInputLabel-root": {
    fontSize: fontSize.label,
  },
});

// ═══════════════════════════════════════════════════════════════
// STEPPER PRIMITIVES
// ═══════════════════════════════════════════════════════════════

/** Stepper container — horizontal row */
export const StepperRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: spacing.sm,
  padding: `${spacing.sm} 0`,
});

/** Individual step indicator */
export const StepIndicator = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "completed",
})<{ active?: boolean; completed?: boolean }>(({ active, completed }) => ({
  display: "flex",
  alignItems: "center",
  gap: spacing.xs,
  padding: `2px ${spacing.md}`,
  borderRadius: radius.xl,
  fontSize: fontSize.label,
  fontWeight: 600,
  color: completed ? colors.textOnDark : active ? colors.textOnDark : "rgba(255,255,255,0.5)",
  backgroundColor: completed ? colors.valid : active ? colors.info : "rgba(255,255,255,0.15)",
  transition: "all 0.2s ease",
}));

// ═══════════════════════════════════════════════════════════════
// STATUS PRIMITIVES
// ═══════════════════════════════════════════════════════════════

/** Taxieren stamp badge */
export const TaxierenBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== "taxiert",
})<{ taxiert?: boolean }>(({ taxiert }) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: `1px ${spacing.sm}`,
  borderRadius: radius.xs,
  fontSize: fontSize.label,
  fontWeight: 600,
  color: taxiert ? colors.valid : colors.textMuted,
  border: `1px solid ${taxiert ? colors.valid : colors.borderLight}`,
  whiteSpace: "nowrap",
}));
